import type { HealthRecord, UserProfile, CareProvider, PatientReportedConcern } from "@/shared/types";

export const sampleProfiles: UserProfile[] = [
  {
    id: "profile-1",
    firstName: "Sarah",
    lastName: "Johnson",
    dateOfBirth: "1985-06-15",
    relationship: "self",
    gender: "female",
    isMinor: false,
    isPrimary: true,
    addedDate: "2024-01-01",
    lastAccessed: "2026-03-28",
  },
  {
    id: "profile-2",
    firstName: "Emma",
    lastName: "Johnson",
    dateOfBirth: "2018-09-22",
    relationship: "child",
    gender: "female",
    isMinor: true,
    isPrimary: false,
    addedDate: "2024-01-15",
    lastAccessed: "2026-03-25",
  },
  {
    id: "profile-3",
    firstName: "Liam",
    lastName: "Johnson",
    dateOfBirth: "2020-03-10",
    relationship: "child",
    gender: "male",
    isMinor: true,
    isPrimary: false,
    addedDate: "2024-01-15",
    lastAccessed: "2026-03-20",
  },
  {
    id: "profile-4",
    firstName: "Robert",
    lastName: "Johnson",
    dateOfBirth: "1983-11-08",
    relationship: "spouse",
    gender: "male",
    isMinor: false,
    isPrimary: false,
    addedDate: "2024-02-01",
    lastAccessed: "2026-03-15",
  },
];

export const samplePatientConcerns: PatientReportedConcern[] = [
  {
    id: "pc-001",
    title: "Recurring headaches after starting new medication",
    category: "side_effect",
    urgency: "soon",
    status: "acknowledged",
    reportedDate: "2026-03-25",
    description: "I've been experiencing headaches almost every afternoon since I started taking Lisinopril about 2 weeks ago. The headaches usually start around 2-3 PM and last for several hours. Over-the-counter pain relievers help somewhat but I'm concerned this might be related to the new blood pressure medication.",
    duration: "2 weeks",
    frequency: "Daily",
    triggers: ["Afternoon", "After lunch"],
    relievingFactors: ["Rest", "Tylenol", "Dark room"],
    associatedSymptoms: ["Mild dizziness", "Fatigue"],
    impactOnDaily: "moderate",
    wantToDiscuss: true,
  },
  {
    id: "pc-002",
    title: "Question about diet restrictions",
    category: "question",
    urgency: "routine",
    status: "discussed",
    reportedDate: "2026-03-15",
    description: "I'm confused about what foods I should avoid with my diabetes and high blood pressure. Are there specific foods that affect both conditions? I want to make sure I'm following the right diet plan.",
    impactOnDaily: "none",
    wantToDiscuss: true,
    providerResponse: {
      respondedBy: "Dr. Sarah Chen",
      respondedAt: "2026-03-18",
      message: "Great question! For managing both diabetes and hypertension, focus on reducing sodium (less than 2,300mg daily) and monitoring carbohydrates. I recommend the DASH diet which works well for both conditions.",
      recommendations: [
        "Limit processed foods and restaurant meals",
        "Choose whole grains over refined grains",
        "Eat plenty of vegetables and lean proteins",
        "Schedule appointment with our nutritionist"
      ],
    },
  },
  {
    id: "pc-003",
    title: "Increased anxiety and trouble sleeping",
    category: "mental_health",
    urgency: "soon",
    status: "pending",
    reportedDate: "2026-03-27",
    description: "Over the past month, I've been feeling more anxious than usual, especially at night. I'm having trouble falling asleep and staying asleep. I wake up multiple times during the night and feel tired during the day. This started around the time of some stressful work changes.",
    duration: "1 month",
    frequency: "Every night",
    triggers: ["Work stress", "Evening time", "Thinking about deadlines"],
    relievingFactors: ["Reading", "Warm bath", "Breathing exercises"],
    associatedSymptoms: ["Racing thoughts", "Fatigue", "Difficulty concentrating"],
    impactOnDaily: "moderate",
    wantToDiscuss: true,
  },
  {
    id: "pc-004",
    title: "Joint stiffness in the morning",
    category: "symptom",
    urgency: "routine",
    status: "pending",
    reportedDate: "2026-03-28",
    description: "My hands and knees feel stiff when I wake up in the morning. It takes about 30-45 minutes of moving around before the stiffness goes away. I don't recall any injury and this has been gradually getting worse over the past few months.",
    duration: "3 months",
    frequency: "Every morning",
    triggers: ["Morning", "Cold weather", "After sitting for long periods"],
    relievingFactors: ["Movement", "Warm shower", "Gentle stretching"],
    impactOnDaily: "mild",
    wantToDiscuss: true,
  },
  {
    id: "pc-005",
    title: "Follow up on cholesterol medication",
    category: "question",
    urgency: "routine",
    status: "resolved",
    reportedDate: "2026-02-10",
    description: "Wanted to know when I should get my cholesterol rechecked after starting the statin medication.",
    impactOnDaily: "none",
    wantToDiscuss: false,
    providerResponse: {
      respondedBy: "Dr. Sarah Chen",
      respondedAt: "2026-02-12",
      message: "We typically recheck cholesterol levels 6-8 weeks after starting a statin. I've scheduled a lab order for you - you can complete the fasting blood test anytime in the next 2 weeks.",
      recommendations: [
        "Complete fasting cholesterol panel at Quest Diagnostics",
        "Fast for 9-12 hours before the test",
        "Continue taking medication as prescribed"
      ],
    },
  },
];

export const sampleCareProviders: CareProvider[] = [
  {
    id: "provider-1",
    name: "Dr. Sarah Chen",
    credentials: "MD, FACP",
    specialty: "Internal Medicine",
    role: "primary_care",
    facility: "Westside Medical Center",
    address: "123 Healthcare Blvd, Suite 100, San Francisco, CA 94102",
    phone: "(415) 555-0100",
    fax: "(415) 555-0101",
    email: "dr.chen@westsidemedical.com",
    npi: "1234567890",
    lastSeenDate: "2026-03-15",
    nextAppointment: "2026-06-15",
    notes: "Primary care clinician since 2020",
    isAcceptingNewPatients: true,
    insuranceAccepted: ["Blue Cross PPO", "Aetna", "United Healthcare", "Medicare"],
  },
  {
    id: "provider-2",
    name: "Dr. Michael Rodriguez",
    credentials: "MD, FACC",
    specialty: "Cardiology",
    subSpecialty: "Interventional Cardiology",
    role: "specialist",
    facility: "Heart & Vascular Institute",
    address: "456 Cardiac Way, Suite 300, San Francisco, CA 94103",
    phone: "(415) 555-0200",
    fax: "(415) 555-0201",
    email: "m.rodriguez@heartvasc.com",
    npi: "2345678901",
    lastSeenDate: "2026-02-28",
    nextAppointment: "2026-05-28",
    notes: "Referred by Dr. Chen for hypertension management",
    isAcceptingNewPatients: true,
    insuranceAccepted: ["Blue Cross PPO", "Aetna", "United Healthcare"],
  },
  {
    id: "provider-3",
    name: "Dr. Emily Watson",
    credentials: "MD",
    specialty: "Endocrinology",
    subSpecialty: "Diabetes Management",
    role: "specialist",
    facility: "Diabetes & Metabolism Center",
    address: "789 Endocrine Dr, Suite 200, San Francisco, CA 94104",
    phone: "(415) 555-0300",
    email: "e.watson@diabetescenter.com",
    npi: "3456789012",
    lastSeenDate: "2026-01-10",
    notes: "Managing Type 2 Diabetes since 2023",
    isAcceptingNewPatients: false,
    insuranceAccepted: ["Blue Cross PPO", "Aetna"],
  },
  {
    id: "provider-4",
    name: "Dr. James Park",
    credentials: "DDS",
    specialty: "General Dentistry",
    role: "dentist",
    facility: "Smile Dental Care",
    address: "321 Dental Plaza, San Francisco, CA 94105",
    phone: "(415) 555-0400",
    lastSeenDate: "2025-12-05",
    nextAppointment: "2026-06-05",
    isAcceptingNewPatients: true,
    insuranceAccepted: ["Delta Dental", "MetLife Dental"],
  },
  {
    id: "provider-5",
    name: "Dr. Lisa Thompson",
    credentials: "OD",
    specialty: "Optometry",
    role: "optometrist",
    facility: "Clear Vision Eye Care",
    address: "555 Vision Lane, San Francisco, CA 94106",
    phone: "(415) 555-0500",
    lastSeenDate: "2025-10-20",
    isAcceptingNewPatients: true,
    insuranceAccepted: ["VSP", "EyeMed"],
  },
  {
    id: "provider-6",
    name: "Dr. Amanda Foster",
    credentials: "PhD, LMFT",
    specialty: "Psychology",
    subSpecialty: "Cognitive Behavioral Therapy",
    role: "therapist",
    facility: "Mindful Wellness Center",
    address: "888 Mental Health Way, Suite 400, San Francisco, CA 94107",
    phone: "(415) 555-0600",
    email: "a.foster@mindfulwellness.com",
    lastSeenDate: "2026-03-20",
    nextAppointment: "2026-04-03",
    notes: "Weekly therapy sessions for anxiety management",
    isAcceptingNewPatients: false,
    insuranceAccepted: ["Blue Cross PPO", "Aetna", "United Healthcare"],
  },
  {
    id: "provider-7",
    name: "Dr. Robert Kim",
    credentials: "MD, FAAOS",
    specialty: "Orthopedic Surgery",
    subSpecialty: "Sports Medicine",
    role: "surgeon",
    facility: "Bay Area Orthopedics",
    address: "999 Bone & Joint Blvd, San Francisco, CA 94108",
    phone: "(415) 555-0700",
    fax: "(415) 555-0701",
    npi: "4567890123",
    lastSeenDate: "2025-08-15",
    notes: "Consulted for knee pain, no surgery needed",
    isAcceptingNewPatients: true,
    insuranceAccepted: ["Blue Cross PPO", "Aetna", "United Healthcare", "Medicare"],
  },
];

export const sampleRecords: HealthRecord[] = [
  {
    id: "1",
    type: "lab_result",
    title: "Complete Blood Count (CBC)",
    date: "2026-03-15",
    provider: "Dr. Sarah Chen",
    facility: "Memorial Health Center",
    summary: "Routine blood work showing overall healthy results with one value slightly outside normal range.",
    plainLanguageSummary: "Your blood test came back mostly normal! Your white blood cells, which help fight infections, are slightly elevated. This could mean your body was recently fighting off a minor cold or infection. Nothing to worry about, but worth mentioning at your next visit.",
    status: "attention",
    details: [
      {
        label: "White Blood Cells (WBC)",
        value: "11.2",
        unit: "K/uL",
        normalRange: "4.5-11.0",
        status: "high",
        explanation: "White blood cells are your body's infection fighters. Your level is just slightly above normal, which often happens when you're getting over a cold or minor infection."
      },
      {
        label: "Red Blood Cells (RBC)",
        value: "4.8",
        unit: "M/uL",
        normalRange: "4.5-5.5",
        status: "normal",
        explanation: "Red blood cells carry oxygen throughout your body. Your level is perfectly normal, which means your body is getting the oxygen it needs."
      },
      {
        label: "Hemoglobin",
        value: "14.2",
        unit: "g/dL",
        normalRange: "12.0-16.0",
        status: "normal",
        explanation: "Hemoglobin is the protein in red blood cells that carries oxygen. Your level is healthy, indicating good oxygen delivery to your tissues."
      },
      {
        label: "Platelets",
        value: "245",
        unit: "K/uL",
        normalRange: "150-400",
        status: "normal",
        explanation: "Platelets help your blood clot when you get a cut. Your level is well within the normal range."
      }
    ]
  },
  {
    id: "2",
    type: "prescription",
    title: "Lisinopril 10mg",
    date: "2026-03-10",
    provider: "Dr. Michael Ross",
    facility: "Cardiology Associates",
    summary: "Blood pressure medication prescribed for mild hypertension.",
    plainLanguageSummary: "Your doctor has prescribed a common blood pressure medication. It works by relaxing your blood vessels, making it easier for your heart to pump blood. Take it once daily, preferably in the morning.",
    status: "normal",
    details: [
      {
        label: "Dosage",
        value: "10mg",
        status: "normal",
        explanation: "This is a starting dose. Your doctor may adjust it based on how your blood pressure responds."
      },
      {
        label: "Frequency",
        value: "Once daily",
        status: "normal",
        explanation: "Take one tablet each morning. Try to take it at the same time every day for best results."
      },
      {
        label: "Purpose",
        value: "Blood pressure control",
        status: "normal",
        explanation: "This medication helps lower blood pressure by relaxing blood vessels. Lower blood pressure reduces strain on your heart."
      }
    ]
  },
  {
    id: "3",
    type: "visit_summary",
    title: "Annual Physical Exam",
    date: "2026-02-28",
    provider: "Dr. Sarah Chen",
    facility: "Memorial Health Center",
    summary: "Comprehensive annual checkup with overall positive findings.",
    plainLanguageSummary: "Great news! Your annual physical shows you're in good health. Your heart, lungs, and reflexes all checked out fine. Your doctor noted your blood pressure was a bit high, so she referred you to a cardiologist for a follow-up. Keep up the good work with exercise and healthy eating!",
    status: "normal",
    details: [
      {
        label: "Blood Pressure",
        value: "138/88",
        unit: "mmHg",
        normalRange: "Less than 120/80",
        status: "high",
        explanation: "Your blood pressure is slightly elevated. The top number (138) shows the pressure when your heart beats. The bottom number (88) shows pressure when your heart rests between beats."
      },
      {
        label: "Heart Rate",
        value: "72",
        unit: "bpm",
        normalRange: "60-100",
        status: "normal",
        explanation: "Your heart rate is the number of times your heart beats per minute. 72 is right in the healthy range!"
      },
      {
        label: "BMI",
        value: "24.5",
        unit: "kg/m²",
        normalRange: "18.5-24.9",
        status: "normal",
        explanation: "Body Mass Index (BMI) is a measure of body fat based on height and weight. Your BMI is in the healthy range."
      },
      {
        label: "Temperature",
        value: "98.6",
        unit: "°F",
        normalRange: "97.8-99.1",
        status: "normal",
        explanation: "Your body temperature is perfectly normal."
      }
    ]
  },
  {
    id: "4",
    type: "immunization",
    title: "Influenza Vaccine",
    date: "2025-10-15",
    provider: "Nurse Amy Wilson",
    facility: "CVS Pharmacy",
    summary: "Annual flu shot administered successfully.",
    plainLanguageSummary: "You received your annual flu shot! It takes about 2 weeks for your body to build full protection. You might notice some soreness at the injection site for a day or two - this is completely normal and shows your immune system is responding.",
    status: "normal",
    details: [
      {
        label: "Vaccine Type",
        value: "Quadrivalent",
        status: "normal",
        explanation: "This vaccine protects against four different strains of flu that are expected to be common this season."
      },
      {
        label: "Lot Number",
        value: "FLU2025-4892",
        status: "normal",
        explanation: "This is the batch number of your vaccine, recorded for safety tracking purposes."
      },
      {
        label: "Next Due",
        value: "October 2026",
        status: "normal",
        explanation: "Flu vaccines are recommended annually since the virus changes each year."
      }
    ]
  },
  {
    id: "5",
    type: "imaging",
    title: "Chest X-Ray",
    date: "2026-01-20",
    provider: "Dr. James Park",
    facility: "Memorial Health Center - Radiology",
    summary: "Routine chest X-ray with normal findings.",
    plainLanguageSummary: "Your chest X-ray looks great! The radiologist examined your lungs, heart, and chest bones - everything appears normal. There are no signs of infection, fluid buildup, or any concerning masses. Your heart size is normal too.",
    status: "normal",
    details: [
      {
        label: "Lungs",
        value: "Clear",
        status: "normal",
        explanation: "Your lungs show no signs of infection, fluid, or abnormal masses. They're expanding properly."
      },
      {
        label: "Heart Size",
        value: "Normal",
        status: "normal",
        explanation: "Your heart appears to be a normal size for your body, suggesting it's pumping efficiently."
      },
      {
        label: "Bones",
        value: "No fractures",
        status: "normal",
        explanation: "Your ribs and spine visible on the X-ray show no breaks or concerning changes."
      }
    ]
  }
];

export const medicalTerms: Record<string, { definition: string; simple: string }> = {
  "CBC": {
    definition: "Complete Blood Count - A blood test that measures red blood cells, white blood cells, hemoglobin, hematocrit, and platelets.",
    simple: "A blood test that counts the different types of cells in your blood to check your overall health."
  },
  "WBC": {
    definition: "White Blood Cells - Cells that are part of the immune system and help fight infections.",
    simple: "Cells that help your body fight off germs and infections."
  },
  "RBC": {
    definition: "Red Blood Cells - Cells that carry oxygen from your lungs to the rest of your body.",
    simple: "Cells that carry oxygen throughout your body."
  },
  "Hemoglobin": {
    definition: "A protein in red blood cells that binds to oxygen and carries it throughout the body.",
    simple: "The part of your blood that holds onto oxygen and delivers it where your body needs it."
  },
  "Hypertension": {
    definition: "Chronic high blood pressure, defined as systolic pressure ≥130 mmHg or diastolic pressure ≥80 mmHg.",
    simple: "High blood pressure - when the force of blood against your artery walls is too high over time."
  },
  "BMI": {
    definition: "Body Mass Index - A measure of body fat based on height and weight.",
    simple: "A number that helps estimate if you're at a healthy weight for your height."
  },
  "Quadrivalent": {
    definition: "A vaccine that protects against four different strains of a virus.",
    simple: "A vaccine that protects against four different types of the flu virus."
  }
};
